import { lumi } from '@/lib/lumi'
import { getListingType, getSaleType } from '@/lib/games'
import { publicCardPayload, sanitizeCards } from '@/lib/publicData'
import { useCallback, useEffect, useState } from 'react'

export interface Card {
  _id: string
  userId: string
  title: string
  game?: string
  listingType?: string
  productType?: string
  imageUrl: string
  price?: number
  condition?: string
  description?: string
  tcgplayerUrl?: string
  ebayUrl?: string
  saleType?: string
  forSale?: boolean
  creator: string
  createdAt: string
  updatedAt: string
}

export function useCards(userId?: string) {
  const [cards, setCards] = useState<Card[]>([])
  const [loading, setLoading] = useState(false)

  const fetchCards = useCallback(async () => {
    if (!userId) {
      setCards([])
      setLoading(false)
      return
    }
    setLoading(true)
    try {
      const { list } = await lumi.entities.cards.list({
        filter: { userId },
        sort: { createdAt: -1 },
      })
      setCards(sanitizeCards((list as Card[]) || []))
    } catch (error) {
      console.error('Failed to fetch cards:', error)
    } finally {
      setLoading(false)
    }
  }, [userId])

  useEffect(() => {
    fetchCards()
  }, [fetchCards])

  const requireOwner = () => {
    const ownerId = lumi.auth.user?.userId
    if (!lumi.auth.isAuthenticated || !ownerId) {
      throw new Error('Sign in to manage your listings.')
    }
    if (!userId || userId !== ownerId) {
      throw new Error('You can only manage listings on your own store.')
    }
    return ownerId
  }

  const createCard = async (data: Partial<Card>) => {
    const ownerId = requireOwner()
    const now = new Date().toISOString()
    const listingType = getListingType(data.listingType)
    try {
      const created = await lumi.entities.cards.create(publicCardPayload({
        userId: ownerId,
        title: data.title,
        game: 'Pokemon',
        listingType,
        imageUrl: data.imageUrl,
        price: data.price ?? undefined,
        condition: data.condition || (listingType === 'sealed' ? 'Factory Sealed' : 'Near Mint'),
        description: data.description || '',
        tcgplayerUrl: data.tcgplayerUrl || '',
        ebayUrl: data.ebayUrl || '',
        saleType: getSaleType(data.saleType),
        forSale: data.forSale ?? true,
        createdAt: now,
        updatedAt: now,
        ...(listingType === 'sealed' ? { productType: data.productType || 'Other sealed' } : {}),
      }))
      await fetchCards()
      return created
    } catch (error) {
      console.error('Failed to create card:', error)
      throw error
    }
  }

  const updateCard = async (id: string, data: Partial<Card>) => {
    const ownerId = requireOwner()
    const existing = cards.find(card => card._id === id)
    if (existing && existing.userId !== ownerId) {
      throw new Error('You can only edit your own listings.')
    }
    try {
      const listingType = data.listingType ? getListingType(data.listingType) : undefined
      const payload: Record<string, unknown> = publicCardPayload({
        ...data,
        userId: ownerId,
        game: 'Pokemon',
        updatedAt: new Date().toISOString(),
      })
      if (listingType) payload.listingType = listingType
      if (listingType === 'sealed') payload.productType = data.productType || 'Other sealed'
      if (data.saleType) payload.saleType = getSaleType(data.saleType)
      const updated = await lumi.entities.cards.update(id, payload)
      await fetchCards()
      return updated
    } catch (error) {
      console.error('Failed to update card:', error)
      throw error
    }
  }

  const deleteCard = async (id: string) => {
    const ownerId = requireOwner()
    const existing = cards.find(card => card._id === id)
    if (existing && existing.userId !== ownerId) {
      throw new Error('You can only remove your own listings.')
    }
    try {
      await lumi.entities.cards.delete(id)
      await fetchCards()
    } catch (error) {
      console.error('Failed to delete card:', error)
      throw error
    }
  }

  return { cards, loading, createCard, updateCard, deleteCard, refetch: fetchCards }
}

export function useAllCards(listingFilter?: string) {
  const [cards, setCards] = useState<Card[]>([])
  const [loading, setLoading] = useState(false)

  const fetchAll = useCallback(async () => {
    setLoading(true)
    try {
      const filter: Record<string, unknown> = { game: 'Pokemon' }
      if (listingFilter && listingFilter !== 'All') filter.listingType = listingFilter
      const { list } = await lumi.entities.cards.list({
        filter,
        sort: { createdAt: -1 },
        limit: 200,
      })
      const next = sanitizeCards((list as Card[]) || []).filter(item => {
        if (!listingFilter || listingFilter === 'All') return true
        return getListingType(item.listingType) === listingFilter
      })
      setCards(next)
    } catch (error) {
      console.error('Failed to fetch cards feed:', error)
    } finally {
      setLoading(false)
    }
  }, [listingFilter])

  useEffect(() => {
    fetchAll()
  }, [fetchAll])

  return { cards, loading, refetch: fetchAll }
}
